// ─────────────────────────────────────────────
//  NoAccessPage — an authenticated, NON-sandbox account with no facility.
//
//  This state exists because a self-provisioned profile claims NO facility
//  (isSafeSelfProfile in firestore.rules, buildDefaultProfile in config/roles):
//  facility membership is the whole authorization model and is granted by an
//  administrator, never claimed. So the account is genuinely valid and simply
//  has nothing to show yet.
//
//  The screen therefore says exactly that — an empty board would read as a
//  broken app, and "permission denied" would read as a mistake the person
//  could fix. Signing out is the only action available, so it is the only
//  control on the page.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { logout } from "../firebase/auth";
import { exitSandboxOnSignOut } from "../sandbox/exit";
import { Card, Button } from "../components";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

export default function NoAccessPage() {
  const [error, setError] = useState("");
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setError("");
    setSigningOut(true);
    try {
      await logout();
      // A demo session that signs out must not leave the flag behind.
      exitSandboxOnSignOut();
    } catch (err) {
      console.error("Sign-out failed:", err);
      setError("Could not sign out. Check your connection and try again.");
      setSigningOut(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <Card variant="elevated">
          <h1 style={styles.heading}>No facility yet</h1>
          <p style={styles.body}>
            Your account is ready, but it is not connected to a facility yet. An
            administrator needs to add you before you can see a board.
          </p>
          {error ? (
            <div role="alert" style={styles.error}>{error}</div>
          ) : null}
          <Button
            variant="secondary"
            size="full"
            onClick={handleSignOut}
            loading={signingOut}
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </Button>
        </Card>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: color.surface.base,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.md,
  },
  shell: { width: 400, maxWidth: "95vw" },
  heading: {
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    fontFamily: font.family,
    lineHeight: font.lineHeight.tight,
    margin: 0,
  },
  body: {
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
    fontFamily: font.family,
    margin: `${space.sm}px 0 ${space.lg}px`,
  },
  error: {
    background: color.surface.error,
    border: "1px solid " + color.accent.error,
    // Ink, not accent.error: #dc2626 on this ground is 4.41:1 and FAILS AA for
    // normal text (tokens.js used to assert 4.95:1 and pass — it was wrong, and
    // four screens had been consolidated onto that sentence). accent.error stays
    // correct as the BORDER above; it is only as text that it falls short.
    color: color.surface.errorInk,
    borderRadius: radius.sm,
    padding: "8px 12px",
    fontSize: font.size.label,
    marginBottom: space.sm,
    fontFamily: font.family,
  },
};
